import React, { useRef, useState } from 'react';
import { Table, Tabs, Tag, Tooltip, Card, Statistic, Row, Col, Alert, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getColumnSearchProps } from '../../common/TableFilter';
import FlexButton from '../../common/FlexButton';
import { useGlobalContext } from '../../../App';
import XLSX from 'xlsx';
import '../../../css/table.css';

const { TabPane } = Tabs;

// ===== 所有字段定義（對應ScheduleOrder全部字段） =====
const ALL_FIELDS = [
    { key: 'service_date', label: '服務日期' },
    { key: 'service_time', label: '服務時間' },
    { key: 'customer_ID', label: '客戶編號' },
    { key: 'name', label: '客戶名稱' },
    { key: 'company_ID', label: '集團公司編號' },
    { key: 'address', label: '地址' },
    { key: 'type', label: '服務種類' },
    { key: 'overtime_remark', label: '加班備註' },
    { key: 'staff', label: '技術員' },
    { key: 'overtime', label: '「加班」資料' },
    { key: 'money_type', label: '「佣金種類」資料' },
    { key: 'money_percent', label: '「佣金%」資料' },
    { key: 'period', label: '「服務週期」資料' },
    { key: 'weekday', label: '「平日」資料' },
    { key: 'blacklist', label: '黑名單工作人員' },
    { key: 'important', label: '重要事項(變更提示)' },
    { key: 'holiday', label: '於假期不用工作' },
    { key: 'order_ID', label: '工作單編號' },
    { key: 'confirmed', label: '已確認' },
    { key: 'remark', label: '備註' },
    { key: 'officeName', label: '所屬辦公室' },
    { key: 'fixture_count', label: '潔具數' },
    { key: 'area', label: '區域' },
    { key: 'reassigned', label: '已重分配' },
    { key: 'original_staff', label: '原技術員' },
];

/**
 * 帶顏色的Excel導出
 * - 已重分配的行標記黃色背景
 * - 未分配的行標記紅色背景
 */
function handleColoredExport(orders, fileName, highlightType) {
    if (!orders || orders.length === 0) return;

    const wb = XLSX.utils.book_new();
    const headers = ALL_FIELDS.map(f => f.label);
    const keys = ALL_FIELDS.map(f => f.key);

    // 構建數據行
    const rows = orders.map(order => {
        const row = {};
        keys.forEach(k => {
            let val = order[k];
            if (k === 'reassigned') val = val ? '是' : '否';
            if (val === null || val === undefined) val = '';
            row[k] = val;
        });
        return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows, { header: keys });

    // 設置列寬
    ws['!cols'] = ALL_FIELDS.map(f => ({ wch: Math.max(f.label.length * 2 + 2, 12) }));

    // 設置表頭行
    const headerRow = XLSX.utils.encode_row(0);
    keys.forEach((k, ci) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c: ci });
        if (!ws[addr]) ws[addr] = {};
        ws[addr].v = headers[ci];
    });

    // 為每行設置樣式（xlsx-style不支持完整樣式，但可以通過設置單元格屬性來實現基本效果）
    // 使用 xlsx 庫的 cell styles（社區版支持有限，這裡用簡單方式標記）
    for (let r = 1; r <= rows.length; r++) {
        let isHighlight = false;
        if (highlightType === 'reassigned') {
            isHighlight = orders[r - 1]?.reassigned === true;
        } else if (highlightType === 'unassigned') {
            isHighlight = true; // 未分配的全部標紅
        }

        if (isHighlight) {
            for (let ci = 0; ci < keys.length; ci++) {
                const addr = XLSX.utils.encode_cell({ r, c: ci });
                if (ws[addr]) {
                    // xlsx社區版對樣式支持有限，設置字體顏色作為標記
                    ws[addr].s = highlightType === 'unassigned'
                        ? { font: { color: { rgb: 'FF0000' } } }
                        : { font: { bold: true, color: { rgb: '996600' } } };
                }
            }
        }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'sheet');

    // 使用 xlsx-style 的替代方案：在數據中直接加顏色標記列
    // 由於純 xlsx 庫樣式支持有限，改用生成帶顏色標記的完整數據
    const wb2 = XLSX.utils.book_new();
    const enrichedHeaders = [...headers];
    const enrichedKeys = [...keys];

    // 為重分配行添加 "★" 前綴標記
    const enrichedRows = orders.map(order => {
        const row = {};
        enrichedKeys.forEach(k => {
            let val = order[k];
            if (k === 'reassigned') val = val ? '✓ 是' : '否';
            if (k === 'staff' && order.reassigned) val = '★ ' + (val || '');
            if (k === 'original_staff' && order.reassigned && val) val = '← ' + val;
            if (val === null || val === undefined) val = '';
            row[k] = val;
        });
        return row;
    });

    const ws2 = XLSX.utils.json_to_sheet(enrichedRows, { header: enrichedKeys });
    ws2['!cols'] = ALL_FIELDS.map(f => ({ wch: Math.max(f.label.length * 2 + 4, 14) }));

    // 設置表頭
    enrichedKeys.forEach((k, ci) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c: ci });
        if (!ws2[addr]) ws2[addr] = {};
        ws2[addr].v = enrichedHeaders[ci];
    });

    XLSX.utils.book_append_sheet(wb2, ws2, '重分配結果');
    XLSX.writeFile(wb2, fileName + '.xlsx');
}

/**
 * 散單重分配結果展示 - 全字段版本
 */
export default () => {
    const { redistributionResult } = useGlobalContext();

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    if (!redistributionResult) {
        return <Alert message="尚未運行重分配，請先完成前述步驟。" type="info" showIcon />;
    }

    const {
        redistributed_orders = [],
        staff_summaries = [],
        unassigned_orders = [],
        reassignment_logs = [],
        total_scattered_orders = 0,
        assigned_count = 0,
        unassigned_count = 0,
        unloaded_count = 0,
        message = '',
    } = redistributionResult;

    // ===== 員工統計摘要表 =====
    const summaryColumns = [
        {
            title: '技術員ID', dataIndex: 'tech_id', key: 'tech_id', width: 120, fixed: 'left',
            ...getColumnSearchProps('tech_id', '技術員ID', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        {
            title: '技術員名稱', dataIndex: 'tech_name', key: 'tech_name', width: 120,
        },
        {
            title: '員工類型', dataIndex: 'staff_type', key: 'staff_type', width: 100,
            render: (type) => {
                const colors = { 'PC': 'blue', 'PC+HY': 'purple', 'HY': 'green', '其他': 'default' };
                return <Tag color={colors[type] || 'default'}>{type}</Tag>;
            },
            filters: [
                { text: 'PC', value: 'PC' },
                { text: 'PC+HY', value: 'PC+HY' },
                { text: 'HY', value: 'HY' },
            ],
            onFilter: (value, record) => record.staff_type === value,
        },
        { title: 'PC單量', dataIndex: 'pc_count', key: 'pc_count', width: 80, sorter: (a, b) => a.pc_count - b.pc_count },
        { title: 'HY單量', dataIndex: 'hy_count', key: 'hy_count', width: 80, sorter: (a, b) => a.hy_count - b.hy_count },
        { title: '總單量', dataIndex: 'total_order_count', key: 'total_order_count', width: 80 },
        { title: '潔具數', dataIndex: 'fixture_count', key: 'fixture_count', width: 80, sorter: (a, b) => a.fixture_count - b.fixture_count },
        { title: '主要區域', dataIndex: 'main_area', key: 'main_area', width: 100 },
        {
            title: 'PC單達標', dataIndex: 'below_min_pc', key: 'below_min_pc', width: 100,
            render: (val) => val ? <Tag color="red">不足</Tag> : <Tag color="green">達標</Tag>,
            filters: [
                { text: '不達標', value: true },
                { text: '達標', value: false },
            ],
            onFilter: (value, record) => record.below_min_pc === value,
        },
        {
            title: '潔具達標', dataIndex: 'above_max_fixture', key: 'above_max_fixture', width: 100,
            render: (val) => val ? <Tag color="red">超標</Tag> : <Tag color="green">達標</Tag>,
        },
        { title: '新分配PC', dataIndex: 'assigned_pc_count', key: 'assigned_pc_count', width: 100 },
        { title: '新分配HY', dataIndex: 'assigned_hy_count', key: 'assigned_hy_count', width: 100 },
        { title: '卸載HY', dataIndex: 'unloaded_hy_count', key: 'unloaded_hy_count', width: 100 },
    ];

    // ===== 分配日誌表 =====
    const logColumns = [
        {
            title: '操作', dataIndex: 'action', key: 'action', width: 80,
            render: (action) => action === 'assign' ?
                <Tag color="blue">分配</Tag> :
                <Tag color="orange">卸載</Tag>,
            filters: [
                { text: '分配', value: 'assign' },
                { text: '卸載', value: 'unload' },
            ],
            onFilter: (value, record) => record.action === value,
        },
        { title: '服務日期', dataIndex: 'service_date', key: 'service_date', width: 120 },
        { title: '客戶編號', dataIndex: 'customer_ID', key: 'customer_ID', width: 120 },
        {
            title: '客戶名稱', dataIndex: 'customer_name', key: 'customer_name', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '類型', dataIndex: 'type', key: 'type', width: 80,
            render: (type) => <Tag color={type === 'PC' ? 'blue' : 'green'}>{type}</Tag>,
        },
        { title: '潔具數', dataIndex: 'fixture_count', key: 'fixture_count', width: 80 },
        { title: '原技術員', dataIndex: 'from_staff', key: 'from_staff', width: 120 },
        { title: '新技術員', dataIndex: 'to_staff', key: 'to_staff', width: 120 },
        { title: '區域', dataIndex: 'area', key: 'area', width: 80 },
        {
            title: '區域匹配', dataIndex: 'region_matched', key: 'region_matched', width: 100,
            render: (val) => val === true ? <Tag color="green">匹配</Tag> : (val === false ? <Tag color="orange">跨區</Tag> : '-'),
        },
        {
            title: '原因', dataIndex: 'reason', key: 'reason', width: 250, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
    ];

    // ===== 重分配後訂單表 - 全字段 =====
    const orderColumns = [
        {
            title: '狀態', dataIndex: 'reassigned', key: 'reassigned', width: 80, fixed: 'left',
            render: (val) => val ? <Tag color="orange">★ 已分配</Tag> : <Tag>原行程</Tag>,
            filters: [
                { text: '已重分配', value: true },
                { text: '原行程', value: false },
            ],
            onFilter: (value, record) => record.reassigned === value,
        },
        {
            title: '服務日期', dataIndex: 'service_date', key: 'service_date', width: 120,
            ...getColumnSearchProps('service_date', '服務日期', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        { title: '服務時間', dataIndex: 'service_time', key: 'service_time', width: 120 },
        {
            title: '客戶編號', dataIndex: 'customer_ID', key: 'customer_ID', width: 120,
            ...getColumnSearchProps('customer_ID', '客戶編號', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        {
            title: '客戶名稱', dataIndex: 'name', key: 'name', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '集團公司編號', dataIndex: 'company_ID', key: 'company_ID', width: 120 },
        {
            title: '地址', dataIndex: 'address', key: 'address', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '服務種類', dataIndex: 'type', key: 'type', width: 100,
            render: (type) => <Tag color={type === 'PC' ? 'blue' : type === 'HY' ? 'green' : 'default'}>{type}</Tag>,
        },
        {
            title: '加班備註', dataIndex: 'overtime_remark', key: 'overtime_remark', width: 120, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '技術員', dataIndex: 'staff', key: 'staff', width: 150, ellipsis: { showTitle: false },
            ...getColumnSearchProps('staff', '技術員', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
            render: (text, record) => {
                const display = record.reassigned ? '★ ' + text : text;
                return <Tooltip placement="topLeft" title={display}><span style={record.reassigned ? { color: '#d48806', fontWeight: 500 } : {}}>{display}</span></Tooltip>;
            },
        },
        { title: '「加班」資料', dataIndex: 'overtime', key: 'overtime', width: 120 },
        { title: '「佣金種類」資料', dataIndex: 'money_type', key: 'money_type', width: 160 },
        { title: '「佣金%」資料', dataIndex: 'money_percent', key: 'money_percent', width: 160 },
        { title: '「服務週期」資料', dataIndex: 'period', key: 'period', width: 160 },
        { title: '「平日」資料', dataIndex: 'weekday', key: 'weekday', width: 120 },
        {
            title: '黑名單工作人員', dataIndex: 'blacklist', key: 'blacklist', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '重要事項(變更提示)', dataIndex: 'important', key: 'important', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '於假期不用工作', dataIndex: 'holiday', key: 'holiday', width: 140 },
        {
            title: '工作單編號', dataIndex: 'order_ID', key: 'order_ID', width: 140,
            ...getColumnSearchProps('order_ID', '工作單編號', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        { title: '已確認', dataIndex: 'confirmed', key: 'confirmed', width: 80 },
        {
            title: '備註', dataIndex: 'remark', key: 'remark', width: 140, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '所屬辦公室', dataIndex: 'officeName', key: 'officeName', width: 120 },
        { title: '潔具數', dataIndex: 'fixture_count', key: 'fixture_count', width: 80 },
        { title: '區域', dataIndex: 'area', key: 'area', width: 80 },
        {
            title: '原技術員', dataIndex: 'original_staff', key: 'original_staff', width: 150, ellipsis: { showTitle: false },
            render: (text) => text ? <Tooltip placement="topLeft" title={text}><span style={{ color: '#999' }}>← {text}</span></Tooltip> : '-',
        },
    ];

    // ===== 未分配散單表 - 全字段 =====
    const unassignedColumns = [
        {
            title: '服務日期', dataIndex: 'service_date', key: 'service_date', width: 120,
            render: (text) => text ? <Tag color="blue">{text}</Tag> : <Tag color="red">未指定</Tag>,
        },
        { title: '服務時間', dataIndex: 'service_time', key: 'service_time', width: 120 },
        {
            title: '客戶編號', dataIndex: 'customer_ID', key: 'customer_ID', width: 120,
            ...getColumnSearchProps('customer_ID', '客戶編號', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        {
            title: '客戶名稱', dataIndex: 'name', key: 'name', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '集團公司編號', dataIndex: 'company_ID', key: 'company_ID', width: 120 },
        {
            title: '地址', dataIndex: 'address', key: 'address', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '服務種類', dataIndex: 'type', key: 'type', width: 100,
            render: (type) => <Tag color={type === 'PC' ? 'blue' : type === 'HY' ? 'green' : 'default'}>{type}</Tag>,
        },
        { title: '加班備註', dataIndex: 'overtime_remark', key: 'overtime_remark', width: 120 },
        {
            title: '技術員', dataIndex: 'staff', key: 'staff', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text || '(無)'}</Tooltip>,
        },
        { title: '「加班」資料', dataIndex: 'overtime', key: 'overtime', width: 120 },
        { title: '「佣金種類」資料', dataIndex: 'money_type', key: 'money_type', width: 160 },
        { title: '「佣金%」資料', dataIndex: 'money_percent', key: 'money_percent', width: 160 },
        { title: '「服務週期」資料', dataIndex: 'period', key: 'period', width: 160 },
        { title: '「平日」資料', dataIndex: 'weekday', key: 'weekday', width: 120 },
        {
            title: '黑名單工作人員', dataIndex: 'blacklist', key: 'blacklist', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '重要事項(變更提示)', dataIndex: 'important', key: 'important', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '於假期不用工作', dataIndex: 'holiday', key: 'holiday', width: 140 },
        {
            title: '工作單編號', dataIndex: 'order_ID', key: 'order_ID', width: 140,
            ...getColumnSearchProps('order_ID', '工作單編號', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        { title: '已確認', dataIndex: 'confirmed', key: 'confirmed', width: 80 },
        {
            title: '備註', dataIndex: 'remark', key: 'remark', width: 140, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '所屬辦公室', dataIndex: 'officeName', key: 'officeName', width: 120 },
        { title: '潔具數', dataIndex: 'fixture_count', key: 'fixture_count', width: 80 },
        { title: '區域', dataIndex: 'area', key: 'area', width: 80 },
    ];

    // 統計數據
    const pcStaffBelowMin = staff_summaries.filter(s => (s.staff_type === 'PC' || s.staff_type === 'PC+HY') && s.below_min_pc).length;
    const pcHyAboveMax = staff_summaries.filter(s => s.above_max_fixture).length;
    const reassignedCount = redistributed_orders.filter(o => o.reassigned).length;

    return (
        <div>
            <Alert
                message={message}
                type={unassigned_count === 0 && pcStaffBelowMin === 0 && pcHyAboveMax === 0 ? 'success' : 'warning'}
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={3}><Statistic title="散單總數" value={total_scattered_orders} /></Col>
                    <Col span={3}><Statistic title="已分配" value={assigned_count} valueStyle={{ color: '#52c41a' }} /></Col>
                    <Col span={3}><Statistic title="未分配" value={unassigned_count} valueStyle={{ color: unassigned_count > 0 ? '#ff4d4f' : '#52c41a' }} /></Col>
                    <Col span={3}><Statistic title="卸載HY" value={unloaded_count} valueStyle={{ color: '#faad14' }} /></Col>
                    <Col span={3}><Statistic title="PC不達標" value={pcStaffBelowMin} valueStyle={{ color: pcStaffBelowMin > 0 ? '#ff4d4f' : '#52c41a' }} /></Col>
                    <Col span={3}><Statistic title="潔具超標" value={pcHyAboveMax} valueStyle={{ color: pcHyAboveMax > 0 ? '#ff4d4f' : '#52c41a' }} /></Col>
                    <Col span={3}><Statistic title="★ 已處理" value={reassignedCount} valueStyle={{ color: '#d48806' }} /></Col>
                </Row>
            </Card>

            <Tabs defaultActiveKey="1">
                <TabPane tab="員工統計" key="1">
                    <FlexButton text="導出員工統計" onClick={() => handleColoredExport(staff_summaries, '員工統計摘要', 'none')} />
                    <Table
                        style={{ marginTop: 16 }}
                        bordered
                        scroll={{ x: 'calc(1200px + 50%)', y: 500 }}
                        columns={summaryColumns}
                        dataSource={staff_summaries.map((s, i) => ({ ...s, key: i }))}
                        size="small"
                        rowClassName={(record) => {
                            if (record.below_min_pc) return 'row_background_3';
                            if (record.above_max_fixture) return 'row_background_4';
                            return '';
                        }}
                    />
                </TabPane>

                <TabPane tab="分配日誌" key="2">
                    <FlexButton text="導出分配日誌" onClick={() => handleColoredExport(reassignment_logs, '分配日誌', 'none')} />
                    <Table
                        style={{ marginTop: 16 }}
                        bordered
                        scroll={{ x: 'calc(1200px + 50%)', y: 500 }}
                        columns={logColumns}
                        dataSource={reassignment_logs.map((l, i) => ({ ...l, key: i }))}
                        size="small"
                    />
                </TabPane>

                <TabPane tab={`重分配後訂單 (${redistributed_orders.length})`} key="3">
                    <FlexButton
                        text="導出重分配後訂單（★ 標記已處理）"
                        onClick={() => handleColoredExport(redistributed_orders, '重分配後訂單', 'reassigned')}
                    />
                    <Table
                        style={{ marginTop: 16 }}
                        bordered
                        scroll={{ x: 'calc(2400px + 50%)', y: 500 }}
                        columns={orderColumns}
                        dataSource={redistributed_orders.map((o, i) => ({ ...o, key: i }))}
                        size="small"
                        rowClassName={(record) => record.reassigned ? 'row_background_2' : ''}
                    />
                </TabPane>

                <TabPane tab={`未分配散單 (${unassigned_count})`} key="4">
                    <FlexButton
                        text="導出未分配散單（紅色標記）"
                        onClick={() => handleColoredExport(unassigned_orders, '未分配散單', 'unassigned')}
                    />
                    <Table
                        style={{ marginTop: 16 }}
                        bordered
                        scroll={{ x: 'calc(2400px + 50%)', y: 500 }}
                        columns={unassignedColumns}
                        dataSource={unassigned_orders.map((o, i) => ({ ...o, key: i }))}
                        size="small"
                        rowClassName={() => 'row_background_3'}
                    />
                </TabPane>
            </Tabs>
        </div>
    );
};
